import {
  CRUDInputElementConstants,
  ECardinality,
  EFormFieldOptionsSourceKind,
  type IActionDefinitionDto,
  type ICRUDFieldOptionsItem,
  type ICRUDFieldOptionsSource,
  type IKType,
  type IKTypeField
} from "@kuika/kuika-cl-model";
import {
  isActionDefinitionVisible,
  type ActionFilterContext
} from "../../../../shared/utility/action-definition-filter";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";

/**
 * What a SelectBox / MultiSelect field's options can be bound to, resolved for the Forms tab.
 *
 * A field whose element type is Select or MultiSelect renders a component with an `options`
 * property, and that property has to be bound to something or the dropdown ships EMPTY. Only
 * two things can bind it in this platform — a hand-entered fixed list, or a list-returning
 * action — and this module works out which of those the user may actually pick, and with which
 * columns, WITHOUT the target screen being open (the Forms tab lives in the process editor).
 *
 * Pure on purpose: everything here is a function of the project's action definitions plus the
 * field being edited, so it is unit-tested directly and the component stays presentational.
 */

/** One selectable column of an action's returned row. */
export interface IOptionColumn {
  name: string;
  /** The column's data type, as the platform's compatibility rule spells it. */
  typeName: string;
}

/**
 * A usable string, or undefined.
 *
 * LOAD-BEARING, not defensive noise. Several fields on these platform DTOs are TYPED
 * `string | undefined` but arrive as `null` from the server (`displayName` is the one that
 * crashed the Forms tab: `null !== undefined` passes an undefined-check and then `.length`
 * throws). Every string read in this module goes through here rather than trusting the type.
 */
export const usableString = (value: string | undefined | null): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return value.length > 0 ? value : undefined;
};

/**
 * Whether a field's element type is one that HAS options. Only these two of the form's element
 * types render a component with an `options` property, so the source picker appears for them
 * and nothing else.
 */
export const isOptionsCapableElementType = (elementType: string | undefined | null): boolean => {
  return elementType === CRUDInputElementConstants.Select || elementType === CRUDInputElementConstants.MultiSelect;
};

/**
 * Whether this action will need its arguments wired before it returns anything.
 *
 * It used to be a FILTER: an action with a required parameter was not offered at all, because the
 * chosen action goes onto the screen's page-init with no arguments and there was nowhere to wire
 * one — an offered-but-empty dropdown is the symptom this feature exists to remove. That
 * restriction is now lifted: the screen builder hosts the platform's own action editors, so the
 * page-init action's parameters are bound in the same panel, and withholding those actions would
 * hide exactly the ones a real screen needs (a list filtered by the current user, a lookup by
 * process value).
 *
 * What remains is a WARNING: the picker says the action needs its parameters bound and where to do
 * it, instead of either hiding it or leaving the user with a silently empty dropdown.
 */
export const actionRequiresParameterBinding = (definition: IActionDefinitionDto): boolean => {
  const parameters = definition.parameters ?? [];
  return parameters.some((parameter): boolean => parameter.isOptionalParameter !== true);
};

/**
 * Whether an action can supply a LIST of rows for a dropdown.
 *
 * Deliberately the property panel's own test, character for character: a MANY cardinality, or a
 * return type of `any`. The `any` half is load-bearing and was missing at first — C# functions,
 * REST calls and AI actions commonly declare `any`, so leaving it out hid exactly the actions a
 * user is most likely to reach for, while the property panel two panes away offered them. A
 * narrower rule here is a divergence, not a safety measure.
 */
const returnsAList = (definition: IActionDefinitionDto): boolean => {
  if (definition.returnCardinality === ECardinality.Many) return true;
  return usableString(definition.returnTypeName)?.toLowerCase() === "any";
};

/**
 * The actions a user may pick as an options source, in display order.
 *
 * Two filters, and both of them are the property panel's rather than ours:
 *  1. `isActionDefinitionVisible` — the platform's single source of truth for "may this surface
 *     offer this action", shared with the property panel's action picker and the MCP tools.
 *  2. {@link returnsAList} — the property panel's own list test.
 *
 * There is no longer a third: the parameter restriction was lifted once the screen builder gained
 * the platform's action editors, so this surface now offers exactly what the property panel does.
 * An action that needs arguments is offered WITH a warning — see
 * {@link actionRequiresParameterBinding}.
 */
export const listSelectableOptionActions = (
  definitions: IActionDefinitionDto[] | undefined,
  filterContext: ActionFilterContext
): IActionDefinitionDto[] => {
  const all: IActionDefinitionDto[] = definitions ?? [];
  return all
    .filter(
      (definition: IActionDefinitionDto): boolean =>
        isActionDefinitionVisible(definition, filterContext) && returnsAList(definition)
    )
    .sort((left: IActionDefinitionDto, right: IActionDefinitionDto): number =>
      optionActionLabel(left).localeCompare(optionActionLabel(right))
    );
};

/**
 * How an action reads in the source picker — its display name, falling back to its name, and to
 * a placeholder when the server sent neither. A label is never allowed to be null: it is sorted
 * on and rendered, and both crash on one.
 */
export const optionActionLabel = (definition: IActionDefinitionDto): string => {
  return usableString(definition.displayName) ?? usableString(definition.name) ?? "Unnamed action";
};

/**
 * The columns of an action's returned row that a dropdown can use, in display order.
 *
 * Scalars only: a list-valued or structured member is a sub-collection, and neither the label
 * shown to the user nor the value stored can be one.
 */
export const listOptionColumns = (definition: IActionDefinitionDto | undefined): IOptionColumn[] => {
  const returnType: IKType | undefined = definition?.returnTypeModel;
  const elements: IKTypeField[] = returnType?.elements ?? [];
  return elements
    .filter((element: IKTypeField): boolean => element.isList !== true && usableString(element.name) !== undefined)
    .map((element: IKTypeField): IOptionColumn => ({
      name: usableString(element.name) as string,
      typeName: usableString(element.type?.name) ?? ""
    }))
    .sort((left: IOptionColumn, right: IOptionColumn): number => left.name.localeCompare(right.name));
};

/**
 * The columns usable as the STORED VALUE for a field of the given data type.
 *
 * Narrower than {@link listOptionColumns} on purpose. The chosen column's value is what the
 * form writes into the process parameter this field is bound to, and this platform has already
 * had an incident where a text value reaching an id-typed slot was seen by NO layer and the
 * engine silently wrote an empty id. So an incompatible column is not offered at all, using
 * the platform's own compatibility rule rather than a second opinion invented here.
 *
 * An unknown field type (a custom field the user has not typed yet) is not a licence to offer
 * everything: with nothing to check against, every column is returned and the caller decides.
 */
export const listValueColumns = (
  definition: IActionDefinitionDto | undefined,
  fieldTypeName: string | undefined | null
): IOptionColumn[] => {
  const columns: IOptionColumn[] = listOptionColumns(definition);
  const targetType: string | undefined = usableString(fieldTypeName);

  if (targetType === undefined) {
    return columns;
  }

  return columns.filter((column: IOptionColumn): boolean =>
    ScreenDesignHelper.getAreTypesCompatible(column.typeName, targetType)
  );
};

/**
 * A fresh fixed-list source. `fieldToDisplay` / `fieldToUseAsKey` are deliberately absent: a
 * fixed list is always keyed `key` / `text`, and the backend binder pins those itself.
 */
export const newFixedListSource = (items: ICRUDFieldOptionsItem[]): ICRUDFieldOptionsSource => ({
  kind: EFormFieldOptionsSourceKind.FixedList,
  fixedItems: items
});

/**
 * A fresh action source, with the columns pre-selected the way the platform guesses them
 * elsewhere: the key by convention, and a display column that is neither the key nor an audit
 * column. Pre-selecting means picking an action alone already yields a working dropdown; the
 * user only has to intervene when the guess is wrong.
 */
export const newActionSource = (
  definition: IActionDefinitionDto,
  fieldTypeName: string | undefined | null
): ICRUDFieldOptionsSource => {
  const valueColumns: IOptionColumn[] = listValueColumns(definition, fieldTypeName);
  const displayColumns: IOptionColumn[] = listOptionColumns(definition);
  const valueColumn: IOptionColumn | undefined = guessValueColumn(valueColumns);
  const displayColumn: IOptionColumn | undefined = guessDisplayColumn(displayColumns, valueColumn?.name);

  return {
    kind: EFormFieldOptionsSourceKind.Action,
    actionDefinitionId: definition.id,
    actionName: definition.name,
    fieldToDisplay: displayColumn?.name,
    fieldToUseAsKey: valueColumn?.name
  };
};

/** Column names that are never a good label — audit noise rather than something a user reads. */
const DISPLAY_EXCLUDED_COLUMNS: ReadonlySet<string> = new Set<string>([
  "id",
  "createdat",
  "createdby",
  "createduser_id",
  "updatedat",
  "updatedby",
  "isdeleted",
  "rowversion"
]);

/** Preferred label columns, best first. */
const DISPLAY_PREFERRED_COLUMNS: readonly string[] = ["name", "title", "label", "description", "text"];

/** The key by convention: a column literally called id, else the first id-typed one, else the first. */
const guessValueColumn = (columns: IOptionColumn[]): IOptionColumn | undefined => {
  const byName: IOptionColumn | undefined = columns.find(
    (column: IOptionColumn): boolean => column.name.toLowerCase() === "id"
  );
  if (byName !== undefined) {
    return byName;
  }
  const byType: IOptionColumn | undefined = columns.find(
    (column: IOptionColumn): boolean => column.typeName.toLowerCase() === "guid"
  );
  return byType ?? columns[0];
};

/** A human-readable label: not the key, not audit noise, preferring the usual names. */
const guessDisplayColumn = (
  columns: IOptionColumn[],
  valueColumnName: string | undefined
): IOptionColumn | undefined => {
  const candidates: IOptionColumn[] = columns.filter(
    (column: IOptionColumn): boolean =>
      column.name !== valueColumnName && !DISPLAY_EXCLUDED_COLUMNS.has(column.name.toLowerCase())
  );

  for (const preferred of DISPLAY_PREFERRED_COLUMNS) {
    const hit: IOptionColumn | undefined = candidates.find(
      (column: IOptionColumn): boolean => column.name.toLowerCase() === preferred
    );
    if (hit !== undefined) {
      return hit;
    }
  }

  const firstText: IOptionColumn | undefined = candidates.find(
    (column: IOptionColumn): boolean => column.typeName.toLowerCase() === "string"
  );
  return firstText ?? candidates[0];
};

/**
 * Whether changing a field's element type should open the options editor straight away.
 *
 * The generated SelectBox ships EMPTY unless a source is declared, and the trigger that sets one is
 * a small button beside the element-type select — easy not to notice. The one moment the question
 * is unmissable is the moment the answer starts mattering, so the editor opens on the transition
 * INTO an options-capable type.
 *
 * It is a transition, not a state: switching SelectBox → MultiSelect must not reopen it, and
 * neither must returning to a field that already has a source. Both would be a modal the user did
 * not ask for, which is how a helpful prompt turns into something people learn to dismiss.
 */
export const shouldPromptForOptionsSource = (
  previousElementType: string | undefined | null,
  nextElementType: string | undefined | null,
  source: ICRUDFieldOptionsSource | null | undefined
): boolean => {
  if (!isOptionsCapableElementType(nextElementType)) return false;
  if (isOptionsCapableElementType(previousElementType)) return false;
  return source == null || source.kind === EFormFieldOptionsSourceKind.None;
};
